import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Hand, Stethoscope, Globe2, Sun, Award, Tv, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Section";
import HeroFilm from "@/components/HeroFilm";
import { AvailableTodayBanner } from "@/components/AvailableTodayBanner";
import HowItWorks from "@/components/HowItWorks";
import BandExplorer from "@/components/BandExplorer";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { LOOP } from "@/lib/marketPageData";

// APPLICATIONS · five opportunities, mirrors the dedicated /applications page order
const APPLICATIONS_TEASER = [
  { icon: Stethoscope, title: "Medical Aesthetics & Dermatology", body: "Post-treatment adherence support.", to: "/clinical" },
  { icon: Sparkles, title: "Consumer Skincare", body: "Sunscreen and routine care, always within reach.", to: "/why" },
  { icon: Hand, title: "Healthcare & Sanitization", body: "Hand hygiene that moves with the caregiver.", to: "/global-health" },
  { icon: Sun, title: "Personal Protection", body: "Sunscreen, repellent and sanitizer, on the wrist.", to: "/products" },
  { icon: Globe2, title: "Global Health", body: "Refillable repellent for vulnerable communities.", to: "/global-health" },
];

const PROVEN_STATS = [
  { icon: Award, value: "~3,000", label: "Devices manufactured" },
  { icon: Tv, value: "Shark Tank", label: "Nationally televised feature" },
  { icon: ShieldCheck, value: "Granted", label: "U.S. patent family" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <SEO {...PAGE_SEO["/"]} path="/" />

      {/* ============================================================ */}
      {/* SECTION 1 · HERO & VISION                                     */}
      {/* ============================================================ */}
      <HeroFilm />

      {/* ============================================================ */}
      {/* SECTION 2 · THE PROVEN PLATFORM                               */}
      {/* ============================================================ */}
      <section className="bg-black border-b border-zinc-800" data-testid="home-proven-platform">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14 md:pb-16 text-center">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-5">
              The Manufactured Dispensing Platform
            </span>
            <h2 className="font-heading font-light tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-5xl text-white">
              Already manufactured. Already <span className="text-accent">on the wrist.</span>
            </h2>
            <p className="font-body text-zinc-300 text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
              Safe Band Pro is WearSpray's patented, refillable wrist dispenser, the manufactured
              delivery foundation the rest of the platform builds on.
            </p>
          </Reveal>
        </div>

        <AvailableTodayBanner />

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {PROVEN_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="flex items-center gap-4 border border-zinc-800 bg-[#0d0d0d] rounded-xl p-6" data-testid={`home-proven-stat-${i}`}>
                  <s.icon size={22} className="text-accent shrink-0" />
                  <div>
                    <p className="font-heading text-xl text-white leading-tight">{s.value}</p>
                    <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-zinc-500 mt-0.5">{s.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <BandExplorer />

        <div className="border-t border-zinc-900">
          <HowItWorks />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20 flex flex-wrap gap-5">
          <Link
            to="/platform"
            data-testid="home-platform-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium text-white border-b border-zinc-700 pb-1 hover:border-accent hover:text-accent transition-colors"
          >
            Explore the full platform
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/patents"
            data-testid="home-patents-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium text-white border-b border-zinc-700 pb-1 hover:border-accent hover:text-accent transition-colors"
          >
            See the patents
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 · CLINICAL & ADHERENCE                              */}
      {/* ============================================================ */}
      <section className="relative border-b border-zinc-800 bg-black overflow-hidden" data-testid="home-clinical-teaser">
        <div className="absolute inset-0 z-0">
          <img
            loading="lazy"
            src="https://images.unsplash.com/photo-1659989693409-5adc97274bed?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="A dermatologist consulting with a patient about a personalized skincare routine"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.14]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-accent border border-accent/40 rounded-full px-3.5 py-1.5 mb-6">
                  <Stethoscope size={12} /> Clinical & Adherence
                </span>
                <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.08] max-w-2xl">
                  The missing link between personalized skincare and patient adherence.
                </h2>
                <p className="font-body text-zinc-300 text-lg leading-relaxed mt-6 max-w-xl">
                  Physicians recommend a personalized routine. Patients leave the office with
                  good intentions. WearSpray's patented wearable platform is designed to connect
                  that recommendation to immediate access, closing the gap between guidance and
                  consistent, everyday application, though the clinical benefit remains to be
                  validated.
                </p>
                <Link
                  to="/clinical"
                  data-testid="home-clinical-teaser-cta"
                  className="group inline-flex items-center gap-2 font-body text-sm font-medium text-black px-8 py-4 mt-9 transition-colors"
                  style={{ background: "#00e5ff" }}
                >
                  Explore the Clinical Opportunity
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 · WEARSPRAY IQ & THE INTELLIGENT ECOSYSTEM           */}
      {/* ============================================================ */}
      <section className="border-b border-zinc-800 bg-[#0a0a0a]" data-testid="home-iq-ecosystem">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <span
                  className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase px-3.5 py-1.5 rounded-full border"
                  style={{ color: "#ff2d78", borderColor: "rgba(255,45,120,0.4)", background: "rgba(255,45,120,0.06)" }}
                >
                  <Sparkles size={12} /> WearSpray IQ
                </span>
                <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.08] mt-6">
                  Skin intelligence.{" "}
                  <span className="text-accent">Your formula within reach.</span>
                </h2>
                <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-xl">
                  WearSpray IQ and advanced sensor integration are in development to help you
                  understand your skin over time and act on that insight as you go about your
                  day. The system is designed to bring together what you notice about your skin,
                  your routine, biometric signals and environmental conditions such as UV
                  exposure. When it is time to apply or reapply, WearSpray IQ can prompt you while
                  your formula is already within reach.
                </p>
                <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5 max-w-xl">
                  That starts with our refillable wrist dispenser and extends to two concepts in
                  development: a reservoir on the back of your phone and a pod that attaches to a
                  smartwatch band or bag strap.
                </p>
                <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight leading-snug text-white mt-10">
                  Your skin history. <span className="text-accent">Your choice to share.</span>
                </h3>
                <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-xl">
                  Over time, your observations, routine and exposure patterns can build a history
                  you can refer to yourself. If you choose, you can share selected insights with
                  your dermatologist to help them understand what happens between visits. You can
                  also choose to share feedback with the brand behind your formula so it can learn
                  how people use its products in everyday life.
                </p>
                <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-xl">
                  Sharing is optional. You decide what to share and with whom.
                </p>
                <div className="flex flex-wrap gap-5 mt-8">
                  <Link
                    to="/intelligence"
                    data-testid="home-intelligence-cta"
                    className="group inline-flex items-center gap-2 font-body text-sm font-medium text-white border-b border-zinc-700 pb-1 hover:border-accent hover:text-accent transition-colors"
                  >
                    See the technology ecosystem
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/iq?src=homepage"
                    data-testid="home-iq-teaser-cta"
                    className="group inline-flex items-center gap-2 font-body text-sm font-medium px-6 py-3 text-white transition-colors"
                    style={{ background: "#ff2d78" }}
                  >
                    Join WearSpray IQ
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <div className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-black flex justify-center">
                  <video
                    src="/video/app-scroll.mp4"
                    poster="/video/app-scroll-poster.jpg"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full max-w-[300px] md:max-w-[340px] h-[280px] md:h-[360px] object-cover"
                    data-testid="home-iq-phone-video"
                  />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white bg-black/70 border border-white/20 rounded-full px-3 py-1.5 backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> In Development
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Concise Sense → Understand → Prompt → Act → Learn overview */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px mt-14 bg-zinc-800 border border-zinc-800">
              {LOOP.map((l, i) => (
                <div key={l.t} className="bg-[#121212] p-5 md:p-7 flex flex-col items-center text-center gap-3" data-testid={`home-loop-${i}`}>
                  <l.Icon size={22} strokeWidth={1.6} style={{ color: l.c }} />
                  <span className="font-mono text-xs tracking-[0.18em] uppercase text-white">{l.t}</span>
                  <p className="font-body text-zinc-500 text-xs leading-relaxed hidden md:block">{l.d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Smartphone reservoir, same patented mechanism in a new form factor */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mt-16 pt-16 border-t border-zinc-800" data-testid="home-phone-reservoir-concept">
            <div className="md:col-span-5">
              <Reveal>
                <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-black w-full max-w-[240px] aspect-[9/20] mx-auto md:mx-0">
                  <video
                    src="/video/cellphone-dispense.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover object-left"
                    data-testid="home-phone-reservoir-video"
                  />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal delay={0.1}>
                <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-zinc-400 border border-zinc-700 rounded-full px-3.5 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400" /> In Development
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white mt-5">
                  One patented mechanism, more than one form factor.
                </h3>
                <p className="font-body text-zinc-400 text-base md:text-lg leading-relaxed mt-4 max-w-xl">
                  WearSpray's patented valve and refillable reservoir, the same mechanism that
                  powers the wrist-worn device shipping today, is the core utility, not a
                  one-shape product. This phone-mounted dispenser design explores adapting that
                  same patented core into a new form factor. It has been designed and is shown
                  here as a concept video ahead of manufacturing.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 · APPLICATIONS                                      */}
      {/* ============================================================ */}
      <section className="border-b border-zinc-800 bg-black" data-testid="home-applications-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">
              Applications
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.05] max-w-3xl">
              One platform. <span className="text-accent">Five opportunities.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px mt-14 bg-zinc-800 border border-zinc-800">
            {APPLICATIONS_TEASER.map((a, i) => (
              <Reveal key={a.title} delay={(i % 5) * 0.06}>
                <Link
                  to={a.to}
                  data-testid={`home-applications-teaser-${i}`}
                  className="group bg-[#121212] p-6 md:p-7 h-full flex flex-col gap-3 hover:bg-[#161616] transition-colors"
                >
                  <a.icon size={22} strokeWidth={1.6} className="text-accent" />
                  <span className="font-mono text-[11px] tracking-[0.1em] uppercase text-white leading-snug">{a.title}</span>
                  <p className="font-body text-zinc-500 text-xs leading-relaxed">{a.body}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/applications"
              data-testid="home-applications-teaser-cta"
              className="group inline-flex items-center gap-2 font-body text-sm font-medium text-white mt-9 border-b border-zinc-700 pb-1 hover:border-accent hover:text-accent transition-colors"
            >
              Explore all applications
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 6 · INTELLECTUAL PROPERTY & STRATEGIC PARTNERSHIPS    */}
      {/* ============================================================ */}
      <section className="relative bg-[#0a0a0a] overflow-hidden" data-testid="home-ip-partnerships">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[40vh] radial-glow opacity-15 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-20 md:pt-28 text-center">
          <Reveal>
            <span
              className="font-heading text-6xl md:text-8xl font-light leading-none block select-none"
              style={{ color: "rgba(0,229,255,0.3)" }}
              aria-hidden="true"
            >
              &ldquo;
            </span>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.35] text-white -mt-6 md:-mt-10">
              The significance is not the sensor alone. It is the patented{" "}
              <span className="text-accent">
                combination of sensing capability with the WearSpray dispensing architecture.
              </span>
            </p>
            <div className="mx-auto mt-10 h-px w-16" style={{ background: "rgba(0,229,255,0.5)" }} />
            <Link
              to="/patents"
              data-testid="home-patent-quote-link"
              className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.22em] uppercase text-zinc-500 hover:text-accent transition-colors mt-6"
            >
              U.S. Patent No. 12,285,084 B1 &middot; Claim 13
              <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-6">
              For Partners & Investors
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tighter leading-tight max-w-4xl mx-auto">
              Already manufactured. <span className="text-accent">Ready to scale together.</span>
            </h2>
            <p className="font-body text-zinc-300 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              WearSpray is exploring licensing, brand integration, clinical collaboration and
              strategic investment, building on a manufactured, patented product foundation
              rather than a concept alone. We're partnering with beauty, skincare, medical
              aesthetics and global health organizations to bring the platform to market
              worldwide.
            </p>
            <Link
              to="/market"
              data-testid="home-market-link"
              className="inline-flex items-center gap-2 font-body text-sm text-zinc-500 hover:text-accent transition-colors mt-5"
            >
              See the market opportunity
              <ArrowRight size={13} />
            </Link>
            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="home-bottom-cta"
                className="inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 hover:bg-[#00b3cc] transition-colors"
              >
                Partner With Us
                <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
